// components/SimpleInteractiveEarth.jsx
"use client";

import { useState, useRef, useEffect } from 'react';

function SimpleInteractiveEarth() {
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastPosition, setLastPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  
  const countryData = [
    { name: "Tanzania", lat: -6.3690, lng: 34.8888, color: "#1EB53A", code: "TZ", top: "52%", left: "57%" },
    { name: "Botswana", lat: -22.3285, lng: 24.6849, color: "#75AADB", code: "BW", top: "65%", left: "55%" },
    { name: "Nigeria", lat: 9.0820, lng: 8.6753, color: "#008751", code: "NG", top: "46%", left: "52%" },
    { name: "Eswatini", lat: -26.5225, lng: 31.4659, color: "#3E5EB9", code: "SZ", top: "68%", left: "56%" },
    { name: "Suriname", lat: 3.9193, lng: -56.0278, color: "#377E3F", code: "SR", top: "48%", left: "28%" },
    { name: "Angola", lat: -11.2027, lng: 17.8739, color: "#CE1126", code: "AO", top: "58%", left: "53%" },
    { name: "Zambia", lat: -13.1339, lng: 27.8493, color: "#198A00", code: "ZM", top: "60%", left: "56%" }
  ];

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setLastPosition({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const deltaX = e.clientX - lastPosition.x;
    const deltaY = e.clientY - lastPosition.y;
    
    setRotation(prev => ({
      x: prev.x + deltaY * 0.5,
      y: prev.y + deltaX * 0.5
    }));
    
    setLastPosition({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    const handleGlobalMouseUp = () => setIsDragging(false);
    const handleGlobalMouseMove = (e) => {
      if (isDragging) handleMouseMove(e);
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('mousemove', handleGlobalMouseMove);

    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('mousemove', handleGlobalMouseMove);
    };
  }, [isDragging, lastPosition]);

  return (
    <div className="relative h-72 rounded-2xl shadow-2xl overflow-hidden bg-gradient-to-br from-blue-50 to-gray-50">
      <div
        ref={containerRef}
        className="relative w-full h-full cursor-grab active:cursor-grabbing"
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        {/* Earth Image with 3D Rotation */}
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            // transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`,
            transition: isDragging ? 'none' : 'transform 0.1s ease-out',
          }}
        >
          <img
            src="/img/globe.webp"
            alt="Earth"
            className="w-full h-full object-contain"
          />
        </div>

        {/* Country Markers */}
        {countryData.map((country) => (
          <div
            key={country.code}
            className="absolute transform -translate-x-1/2 -translate-y-1/2 group"
            style={{ top: country.top, left: country.left }}
          >
            <div className="relative">
              {/* Glowing dot */}
              <div
                className="w-4 h-4 rounded-full animate-pulse shadow-lg"
                style={{ backgroundColor: country.color }}
              >
                <div className="absolute inset-0 rounded-full animate-ping opacity-20" style={{ backgroundColor: country.color }}></div>
              </div>
              
              {/* Tooltip on hover */}
              <div className="absolute left-1/2 bottom-full mb-2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                <div className="bg-white rounded-lg shadow-xl p-3 min-w-[140px] border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-6 rounded overflow-hidden">
                      <img
                        src={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png`}
                        alt={`${country.name} flag`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className="font-semibold text-gray-800">{country.name}</span>
                  </div>
                  <div className="text-xs text-gray-600">Click for details</div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Connection lines */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none ">
          {countryData.map((country) => (
            <line
              key={country.code}
              x1="50%"
              y1="50%"
              x2={country.left}
              y2={country.top}
              stroke={country.color}
              strokeWidth="1"
              strokeOpacity="0.3"
              strokeDasharray="5,5"
            />
          ))}
        </svg>
      </div>

      {/* Controls */}
      <div className="absolute top-4 left-4 flex gap-2">
        {/* <button
          className="px-4 py-2 bg-white/90 backdrop-blur-sm rounded-lg text-blue-600 text-sm hover:bg-white transition-colors shadow-lg flex items-center gap-2"
          onClick={() => setRotation({ x: 0, y: 0 })}
        >
          🔄 Reset View
        </button> */}
      </div>

      {/* Title */}
      <div className="absolute top-4 right-4">
        <div className="bg-white/90 backdrop-blur-sm px-4 py-3 rounded-lg shadow-lg">
          <h2 className="text-blue-700 text-xl font-bold">Global Association</h2>
          {/* <p className="text-gray-600 text-sm">{countryData.length} Member Nations</p> */}
        </div>
      </div>

      {/* Country Flags */}
      <div className="absolute bottom-4 left-0 right-0">
        <div className="flex justify-center">
          <div className="bg-white/95 backdrop-blur-lg rounded-xl px-6 py-3 shadow-xl border border-gray-200">
            <div className="flex gap-4">
              {countryData.map((country) => (
                <div key={country.code} className="flex flex-col items-center">
                  <div className="w-10 h-7 rounded overflow-hidden shadow-md border border-gray-300">
                    <img
                      src={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png`}
                      alt={`${country.code} flag`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="text-xs font-medium text-gray-700 mt-1">{country.code}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Instructions */}
     
    </div>
  );
}

export default SimpleInteractiveEarth;